/**
 * AppShell - The browser game shell that orchestrates the application.
 *
 * The AppShell:
 * - Manages the WebSocket connection lifecycle
 * - Coordinates between ConnectionService and GameStateService
 * - Provides application-level routing state (which screen to show)
 * - Exposes the player ID for authenticated sessions
 *
 * Screens:
 * - 'landing': Initial screen, not connected
 * - 'queue': Player is in the matchmaking queue
 * - 'match': Player is in an active match
 * - 'gameOver': Match has ended
 */

import { ConnectionState, ConnectionStateChangedEvent } from './models/ConnectionState';
import { ConnectionService, ServerMessageHandler, WebSocketFactory } from './services/ConnectionService';
import {
  GameStateService,
  AppGamestate,
  GameStateChangeListener,
} from './services/GameStateService';
import { type QueueMode } from '../shared/models/QueueModes';
import {
  ClientToServerMessage,
  ServerToClientMessage,
  AuthenticateRequest,
  Authenticated,
  AuthFailed,
  JoinQueueRequest,
  LeaveQueueRequest,
  StartSoloMatchRequest,
  PlayMinionRequest,
  SellMinionRequest,
  PlaySpellRequest,
  RepositionMinionsRequest,
  BuyCardRequest,
  UpgradeMinionRequest,
  GetUpgradeOptionsRequest,
  ReconnectRequest,
  GetAchievementsRequest,
  AchievementsResponse,
  AchievementUnlocked,
  GetStatsRequest,
  StatsResponse,
  GetSkinsRequest,
  SetSkinRequest,
  SkinsResponse,
  BuyRuneRequest,
  MarkReadyRequest,
  ResignMatchRequest,
  GetToplistRequest,
  ToplistResponse,
  CreateCoopLobbyRequest,
  JoinCoopLobbyRequest,
  LeaveCoopLobbyRequest,
  SelectCoopMissionRequest,
  SelectCoopSideRequest,
  StartCoopLobbyRequest,
  CreateLobbyRequest,
  JoinLobbyRequest,
  LeaveLobbyRequest,
  KickLobbyPlayerRequest,
  StartLobbyCountdownRequest,
  GetLobbyStateRequest,
  GiveMinionRequest,
  PROTOCOL_VERSION,
} from '../shared/messages/MessageContracts';
import { type LobbySettingsInput } from '../shared/models/Lobby';

// ─── Application Screen Types ───────────────────────────────────────────────

/**
 * The current screen/viewport the app should render.
 */
export type AppScreen =
  | 'landing'
  | 'queue'
  | 'match'
  | 'gameOver'
  | 'coopLobby'
  | 'lobby'      // the unified Original lobby screen
  | 'createGame'; // the Create Game setup screen

// ─── AppShell Configuration ─────────────────────────────────────────────────

export interface AppShellOptions {
  serverUrl: string;
  playerId: string;
  /**
   * Session token for logged-in users. When present, the shell sends an
   * AuthenticateRequest right after the socket opens (and after every
   * reconnect). Guests omit this.
   */
  authToken?: string;
  websocketFactory?: WebSocketFactory;
  maxReconnectAttempts?: number;
  reconnectDelayMs?: number;
}

// ─── AppShell Event Types ───────────────────────────────────────────────────

export interface AppShellEvent {
  type:
    | 'screenChange'
    | 'connectionChange'
    | 'stateChange'
    | 'error'
    | 'authChange'   // { authenticated: true, playerId: string }
    | 'authFailed'   // { error: string } — token rejected by the server
    | 'achievementsUpdate'   // AchievementInfo[] — full list, sorted
    | 'achievementUnlocked'  // AchievementUnlocked — push on unlock
    | 'statsUpdate'          // PlayerStats — the user's current statistics
    | 'skinsUpdate'          // SkinInfo[] — full catalogue with per-user unlocked/selected
    | 'toplistUpdate';       // { entries: ToplistEntry[]; myPlacement: ToplistEntry | null } — Top 100 + own row (null for guests)
  data: unknown;
}

export type AppShellEventListener = (event: AppShellEvent) => void;

// ─── AppShell ───────────────────────────────────────────────────────────────

export class AppShell {
  private _connection: ConnectionService;
  private _gameState: GameStateService;
  private _playerId: string;
  private _authToken: string | null = null;
  private _currentScreen: AppScreen = 'landing';
  private _shellListeners: AppShellEventListener[] = [];
  private _connectionUnsubscribe: (() => void) | null = null;
  private _stateUnsubscribe: (() => void) | null = null;
  private _initialized: boolean = false;
  /** Whether the server has accepted our session token on the CURRENT socket. */
  private _authenticated: boolean = false;
  /**
   * The last match we were IN while connected. Captured on a mid-match
   * connection loss so the RECONNECTED socket can re-attach to the same
   * match (ReconnectRequest) — without this the player would land on the
   * landing page with no way back short of a refresh (the "random coop
   * disconnects" symptom). Cleared once a match genuinely ends.
   */
  private _lastMatchId: string | null = null;

  constructor(options: AppShellOptions) {
    this._playerId = options.playerId;
    this._authToken = options.authToken ?? null;
    this._connection = new ConnectionService(options.serverUrl, {
      websocketFactory: options.websocketFactory,
      maxReconnectAttempts: options.maxReconnectAttempts,
      reconnectDelayMs: options.reconnectDelayMs,
    });
    // The viewer id lets the game state attribute the coop lobby role
    // (host vs member) — the lobby screen renders from it.
    this._gameState = new GameStateService(options.playerId);
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current application screen. */
  get currentScreen(): AppScreen {
    return this._currentScreen;
  }

  /** Returns the current connection state. */
  get connectionState(): ConnectionState {
    return this._connection.state;
  }

  /**
   * Force an immediate reconnection attempt (no backoff wait). The page
   * calls this when it returns to the foreground — an idle/background tab
   * can lose the socket to browser timer throttling, and recovery must not
   * wait for the next backoff tick.
   */
  pokeConnection(): void {
    this._connection.poke();
  }

  /** Returns the player ID. */
  get playerId(): string {
    return this._playerId;
  }

  /** Returns the current game state. */
  get gameState(): AppGamestate {
    return this._gameState.getState();
  }

  /** Returns true if the shell is initialized and running. */
  get isInitialized(): boolean {
    return this._initialized;
  }

  /** Returns true if a match is currently active. */
  get isInMatch(): boolean {
    return this._gameState.isInMatch;
  }

  /** Returns true if the server accepted the session token on this socket. */
  get isAuthenticated(): boolean {
    return this._authenticated;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Initialize the app shell: wire up listeners and start connection.
   */
  initialize(): void {
    if (this._initialized) {
      return;
    }

    // Listen for connection state changes
    this._connectionUnsubscribe = this._connection.onStateChange(
      this._onConnectionStateChanged.bind(this)
    );

    // Listen for game state changes
    this._stateUnsubscribe = this._gameState.onStateChange(
      this._onGameStateChanged.bind(this)
    );

    // Listen for server messages
    this._connection.onMessage(this._onServerMessage.bind(this));

    // Attempt connection
    this._connection.connect();
    this._initialized = true;
  }

  /**
   * Shutdown the app shell: disconnect and clean up.
   */
  shutdown(): void {
    this._connection.disconnect();

    if (this._connectionUnsubscribe) {
      this._connectionUnsubscribe();
      this._connectionUnsubscribe = null;
    }
    if (this._stateUnsubscribe) {
      this._stateUnsubscribe();
      this._stateUnsubscribe = null;
    }

    this._gameState.reset();
    this._updateScreen('landing');
    this._initialized = false;
  }

  // ─── Event Listeners ─────────────────────────────────────────────────────

  /**
   * Subscribe to app shell events.
   * Returns an unsubscribe function.
   */
  onEvent(listener: AppShellEventListener): () => void {
    this._shellListeners.push(listener);
    return () => {
      const index = this._shellListeners.indexOf(listener);
      if (index !== -1) {
        this._shellListeners.splice(index, 1);
      }
    };
  }

  // ─── Player Actions (Client Messages) ────────────────────────────────────

  /**
   * Send a client message through the connection.
   */
  sendMessage(message: ClientToServerMessage): boolean {
    return this._connection.send(message);
  }

  /**
   * Join the matchmaking queue in the given MODE ('fast' — 4 players — or
   * 'long' — 8 players; see the shared QueueModes spec). Defaults to 'fast'.
   * The server stays authoritative: it validates the mode, enforces the
   * one-queue-at-a-time rule, and broadcasts the resulting queue status.
   */
  joinQueue(mode: QueueMode = 'fast'): boolean {
    const message: JoinQueueRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'JoinQueueRequest',
      playerId: this._playerId,
      queueMode: mode,
    };
    return this._connection.send(message);
  }

  /**
   * Leave the matchmaking queue.
   */
  leaveQueue(): boolean {
    const message: LeaveQueueRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'LeaveQueueRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Start a SOLO match (task 191 — the landing page's Singleplayer option):
   * the server creates an 8-player game (this player + 7 bots) INSTANTLY —
   * no queue, no loading. The match arrives via the normal MatchStarted
   * broadcast (the shell's match-state handling flips the screen).
   */
  startSoloMatch(): boolean {
    const message: StartSoloMatchRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'StartSoloMatchRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Play a minion from hand to battlefield.
   * @param targetPosition - Optional battlefield position (0-indexed) to place the minion.
   */
  playMinion(matchId: string, minionInstanceId: string, targetPosition?: number): boolean {
    const message: PlayMinionRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'PlayMinionRequest',
      matchId,
      playerId: this._playerId,
      minionInstanceId,
      targetPosition,
    };
    return this._connection.send(message);
  }

  /**
   * Cast a spell from the hand: the effect goes into play immediately and
   * the card despawns (it never lands on the battlefield). Targeted spells
   * carry the target minion's instance ID; 'battlefield' spells omit it.
   * The server is authoritative — it validates and applies the effect and
   * produces the resulting state (the UI reconciles on the update).
   */
  playSpell(matchId: string, spellInstanceId: string, targetInstanceId?: string): boolean {
    const message: PlaySpellRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'PlaySpellRequest',
      matchId,
      playerId: this._playerId,
      spellInstanceId,
      targetInstanceId,
    };
    return this._connection.send(message);
  }

  /**
   * Sell a minion from the battlefield.
   */
  sellMinion(matchId: string, minionInstanceId: string): boolean {
    const message: SellMinionRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'SellMinionRequest',
      matchId,
      playerId: this._playerId,
      minionInstanceId,
    };
    return this._connection.send(message);
  }

  /**
   * Reposition minions on the battlefield.
   */
  repositionMinions(matchId: string, newOrder: string[]): boolean {
    const message: RepositionMinionsRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'RepositionMinionsRequest',
      matchId,
      playerId: this._playerId,
      newOrder,
    };
    return this._connection.send(message);
  }

  /**
   * Buy a new (random) card into the hand during preparation.
   * The server computes the authoritative cost (base 5 gold, reduced by
   * Discount minions in play, min 2) and validates gold + hand capacity.
   */
  buyCard(matchId: string): boolean {
    const message: BuyCardRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'BuyCardRequest',
      matchId,
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Upgrade a minion on the battlefield.
   * @param selectedAbilityId - For 'ability' branch: the specific ability ID shown to the player (from UpgradeOptionsResponse).
   */
  upgradeMinion(matchId: string, minionInstanceId: string, branchId: string, selectedAbilityId?: string): boolean {
    const message: UpgradeMinionRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'UpgradeMinionRequest',
      matchId,
      playerId: this._playerId,
      minionInstanceId,
      branchId,
      selectedAbilityId,
    };
    return this._connection.send(message);
  }

  /**
   * Request available upgrade options for a minion from the server.
   * The server responds with UpgradeOptionsResponse including the
   * pre-selected ability (randomly chosen).
   */
  getUpgradeOptions(matchId: string, minionInstanceId: string): boolean {
    const message: GetUpgradeOptionsRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetUpgradeOptionsRequest',
      matchId,
      playerId: this._playerId,
      minionInstanceId,
    };
    return this._connection.send(message);
  }

  /**
   * Reconnect after a connection loss.
   */
  reconnect(matchId: string): boolean {
    const message: ReconnectRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'ReconnectRequest',
      playerId: this._playerId,
      matchId,
    };
    return this._connection.send(message);
  }

  /**
   * Request the logged-in user's achievements (achievements page).
   * The server responds with AchievementsResponse (sorted: unlocked first,
   * then closest to unlocking; hidden-unlocked entries redacted) or an
   * error for guests/unauthenticated clients.
   */
  requestAchievements(): boolean {
    const message: GetAchievementsRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetAchievementsRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Request the logged-in user's statistics (stats page).
   * The server responds with StatsResponse (always for the socket's own
   * username) or an error for guests/unauthenticated clients.
   */
  requestStats(): boolean {
    const message: GetStatsRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetStatsRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Request the logged-in user's skins page data. The server responds with
   * SkinsResponse: the full skin catalogue (grouped by class on the client)
   * with per-user `unlocked`/`selected` flags, or an error for
   * guests/unauthenticated clients.
   */
  requestSkins(): boolean {
    const message: GetSkinsRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetSkinsRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Request the Top 100 list (toplist page). Available to EVERYONE —
   * guests included (the server answers with entries always and a null
   * myPlacement for unauthenticated sockets). The server responds with
   * ToplistResponse; the client only renders it.
   */
  requestToplist(): boolean {
    const message: GetToplistRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetToplistRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Select the skin used for one minion class (skins page). The server
   * validates authoritatively (skin exists, right class, unlocked for the
   * user) and answers with the updated SkinsResponse on success.
   */
  setSkin(minionType: string, skinId: string): boolean {
    const message: SetSkinRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'SetSkinRequest',
      playerId: this._playerId,
      minionType,
      skinId,
    };
    return this._connection.send(message);
  }

  /**
   * Buy a SPECIFIC rune from the in-game rune shop during preparation
   * (the 🪬 Buy Rune button opens the shop overlay; the user picks the
   * rune to buy). The server validates authoritatively (known rune, not
   * already purchased this game, enough gold for the catalogue cost);
   * the resulting state arrives via the usual GameStateUpdate
   * (PlayerData.runes.purchased).
   * @param runeId The rune to buy (a shared-catalogue rune ID).
   */
  buyRune(matchId: string, runeId: string): boolean {
    const message: BuyRuneRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'BuyRuneRequest',
      matchId,
      playerId: this._playerId,
      runeId,
    };
    return this._connection.send(message);
  }

  /**
   * Toggle the player's preparation-ready flag (in-match "Ready" button).
   * `ready` is the DESIRED state after the toggle; the server validates
   * (alive + in preparation) and broadcasts the updated count to everyone
   * in the match via ReadyStateUpdate. When all living players are ready,
   * the server starts the combat immediately.
   */
  markReady(matchId: string, ready: boolean): boolean {
    const message: MarkReadyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'MarkReadyRequest',
      matchId,
      playerId: this._playerId,
      ready,
    };
    return this._connection.send(message);
  }

  /**
   * Send a ResignMatchRequest — forfeit the current match. PREPARATION
   * only (the server rejects it during combat and owns every rule: the
   * player is eliminated, the round re-pairs without them, the scheduled
   * opponent spectates, and it counts as a LOSS for the rating). The
   * resulting state arrives via the usual GameStateUpdate / GameOver —
   * there is no per-player reply.
   */
  resignMatch(matchId: string): boolean {
    const message: ResignMatchRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'ResignMatchRequest',
      matchId,
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  // ─── COOP Lobby Actions ────────────────────────────────────────────────

  /**
   * Create a private COOP lobby. The server assigns the host's next
   * uncleared mission (or a random one when everything is cleared) and
   * responds with the authoritative CoopLobbyStateUpdate (the lobby
   * screen renders from it — the invite code, the mission, the seats).
   */
  createCoopLobby(): boolean {
    const message: CreateCoopLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'CreateCoopLobbyRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * Join a COOP lobby with its 6-character invite code (case-insensitive
   * on the server). Server errors (unknown code, full lobby) arrive as
   * the standard ErrorResponse toast.
   */
  joinCoopLobby(code: string): boolean {
    const message: JoinCoopLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'JoinCoopLobbyRequest',
      playerId: this._playerId,
      code,
    };
    return this._connection.send(message);
  }

  /**
   * Leave the viewer's COOP lobby (the host's leave removes the whole
   * lobby; a member's leave frees only their seat — the authoritative
   * state update drives the UI).
   */
  leaveCoopLobby(): boolean {
    const message: LeaveCoopLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'LeaveCoopLobbyRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /**
   * The HOST re-picks the lobby's mission (one of their available
   * missions — the sequential campaign list). Joiners cannot pick.
   */
  selectCoopMission(lobbyCode: string, missionId: string): boolean {
    const message: SelectCoopMissionRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'SelectCoopMissionRequest',
      playerId: this._playerId,
      lobbyCode,
      missionId,
    };
    return this._connection.send(message);
  }

  /**
   * The HOST picks their COMBAT SIDE (which half of the combined team row
   * their minions occupy; the teammate gets the other side). 'left' →
   * the host's copies are first/left and attack first.
   */
  selectCoopSide(lobbyCode: string, side: 'left' | 'right'): boolean {
    const message: SelectCoopSideRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'SelectCoopSideRequest',
      playerId: this._playerId,
      lobbyCode,
      side,
    };
    return this._connection.send(message);
  }

  /**
   * The HOST starts the lobby's match (requires a full seat). The server
   * creates the coop match and both clients receive MatchStarted (the
   * lobby is consumed — the state update with exists: false follows).
   */
  startCoopLobby(lobbyCode: string): boolean {
    const message: StartCoopLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'StartCoopLobbyRequest',
      playerId: this._playerId,
      lobbyCode,
    };
    return this._connection.send(message);
  }

  // ─── Unified lobby intentions (Original + Co-op, the Play menu) ───

  /**
   * Create a unified lobby (the Create Game screen). The server normalizes
   * + validates the settings authoritatively (the ranked invariant is
   * enforced server-side) and responds with the authoritative
   * LobbyStateUpdate (the lobby screen renders from it).
   */
  createLobby(settings: LobbySettingsInput): boolean {
    const message: CreateLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'CreateLobbyRequest',
      playerId: this._playerId,
      ...settings,
    };
    return this._connection.send(message);
  }

  /**
   * Join a lobby by its CODE — the unified entry that resolves BOTH
   * original and co-op lobbies (no type selection). Server errors
   * (unknown code, full, in-game) arrive as the standard ErrorResponse.
   */
  joinLobby(code: string): boolean {
    const message: JoinLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'JoinLobbyRequest',
      playerId: this._playerId,
      code,
    };
    return this._connection.send(message);
  }

  /** Leave the viewer's lobby (host leave closes it; a member frees a seat). */
  leaveLobby(): boolean {
    const message: LeaveLobbyRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'LeaveLobbyRequest',
      playerId: this._playerId,
    };
    return this._connection.send(message);
  }

  /** The HOST kicks a member (server-validated; disabled once IN_GAME). */
  kickLobbyPlayer(lobbyId: string, targetId: string): boolean {
    const message: KickLobbyPlayerRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'KickLobbyPlayerRequest',
      playerId: this._playerId,
      lobbyId,
      targetId,
    };
    return this._connection.send(message);
  }

  /**
   * The HOST starts the 30-second server countdown (WAITING/POST_GAME →
   * COUNTDOWN). The server broadcasts the authoritative countdownEndsAt.
   */
  startLobbyCountdown(lobbyId: string): boolean {
    const message: StartLobbyCountdownRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'StartLobbyCountdownRequest',
      playerId: this._playerId,
      lobbyId,
    };
    return this._connection.send(message);
  }

  /** (Re)sync the lobby snapshot (e.g. after a reload while in a lobby). */
  getLobbyState(lobbyId: string): boolean {
    const message: GetLobbyStateRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GetLobbyStateRequest',
      playerId: this._playerId,
      lobbyId,
    };
    return this._connection.send(message);
  }

  /**
   * GIVE a minion (hand OR battlefield) to the teammate for 5 gold —
   * the coop sell zone's give half. The server is authoritative (coop
   * team match, preparation phase, ownership, gold, teammate hand
   * capacity); the transfer arrives via the usual GameStateUpdate.
   */
  giveMinion(matchId: string, minionInstanceId: string): boolean {
    const message: GiveMinionRequest = {
      version: PROTOCOL_VERSION,
      messageType: 'GiveMinionRequest',
      matchId,
      playerId: this._playerId,
      minionInstanceId,
    };
    return this._connection.send(message);
  }

  // ─── Internal Methods ────────────────────────────────────────────────────

  private _onConnectionStateChanged(event: ConnectionStateChangedEvent): void {
    this._emitEvent({
      type: 'connectionChange',
      data: event,
    });

    // If disconnected, remember the active match (if any) and reset game
    // state + auth (a new socket needs a new AuthenticateRequest because the
    // server tracks auth per socket). The remembered matchId is what lets the
    // reconnected socket re-attach below.
    if (event.currentState === 'disconnected') {
      const activeMatchId = this._gameState.getMatchState().matchId;
      if (activeMatchId) {
        this._lastMatchId = activeMatchId;
      }
      this._gameState.reset();
      this._authenticated = false;
    }

    // Authenticate on (re)connect when we have a token
    if (event.currentState === 'connected' && this._authToken && !this._authenticated) {
      const authMessage: AuthenticateRequest = {
        version: PROTOCOL_VERSION,
        messageType: 'AuthenticateRequest',
        token: this._authToken,
      };
      this._connection.send(authMessage);
    }

    // Re-attach to a match we dropped out of (mid-match connection loss):
    // the server re-sends the authoritative ReconnectState (full match state)
    // and restores the player's broadcast tracking. A stale/ended match just
    // gets an ErrorResponse, which is harmless.
    if (event.currentState === 'connected' && this._lastMatchId) {
      this.reconnect(this._lastMatchId);
    }
  }

  private _onGameStateChanged(state: AppGamestate): void {
    this._emitEvent({
      type: 'stateChange',
      data: state,
    });

    // The match genuinely ended — no point re-attaching to it on a future
    // (re)connect.
    if (state.match.gameOver) {
      this._lastMatchId = null;
    }

    // Update screen based on game state
    this._updateScreenFromState(state);
  }

  private _onServerMessage(message: ServerToClientMessage): void {
    // Auth messages are handled by the shell, not the game state
    // (they are connection-level, not match-level, events).
    if (message.messageType === 'Authenticated') {
      const msg = message as Authenticated;
      this._authenticated = true;
      this._emitEvent({
        type: 'authChange',
        data: { authenticated: true, playerId: msg.playerId },
      });
      return;
    }
    if (message.messageType === 'AuthFailed') {
      const msg = message as AuthFailed;
      this._authenticated = false;
      this._emitEvent({
        type: 'authFailed',
        data: { error: msg.error },
      });
      return;
    }

    // Session-level achievements messages (not match state): forward as
    // shell events for the achievements overlay / unlock toast.
    if (message.messageType === 'AchievementsResponse') {
      const msg = message as AchievementsResponse;
      this._emitEvent({
        type: 'achievementsUpdate',
        data: msg.achievements,
      });
      return;
    }
    if (message.messageType === 'AchievementUnlocked') {
      const msg = message as AchievementUnlocked;
      this._emitEvent({
        type: 'achievementUnlocked',
        data: msg,
      });
      return;
    }

    // Session-level statistics message (not match state): forward as a
    // shell event for the stats overlay.
    if (message.messageType === 'StatsResponse') {
      const msg = message as StatsResponse;
      this._emitEvent({
        type: 'statsUpdate',
        data: msg.stats,
      });
      return;
    }

    // Session-level toplist message (not match state): forward as a shell
    // event for the toplist overlay. Served to everyone (guests get
    // myPlacement: null — they are not ranked).
    if (message.messageType === 'ToplistResponse') {
      const msg = message as ToplistResponse;
      this._emitEvent({
        type: 'toplistUpdate',
        data: { entries: msg.entries, myPlacement: msg.myPlacement },
      });
      return;
    }

    // Session-level skins message (not match state): forward as a shell
    // event for the skins overlay (initial open AND SetSkin confirmation).
    if (message.messageType === 'SkinsResponse') {
      const msg = message as SkinsResponse;
      this._emitEvent({
        type: 'skinsUpdate',
        data: msg.skins,
      });
      return;
    }

    this._gameState.processMessage(message);
  }

  private _updateScreenFromState(state: AppGamestate): void {
    let newScreen: AppScreen = this._currentScreen;

    if (state.match.gameOver) {
      newScreen = 'gameOver';
    } else if (state.match.matchId !== null) {
      newScreen = 'match';
    } else if (state.lobby !== null) {
      // A unified ORIGINAL lobby is active (waiting/countdown/post-game).
      newScreen = 'lobby';
    } else if (state.coopLobby !== null) {
      newScreen = 'coopLobby';
    } else if (state.queue.isInQueue) {
      newScreen = 'queue';
    } else {
      newScreen = 'landing';
    }

    this._updateScreen(newScreen);
  }

  private _updateScreen(newScreen: AppScreen): void {
    if (newScreen !== this._currentScreen) {
      const previousScreen = this._currentScreen;
      this._currentScreen = newScreen;

      this._emitEvent({
        type: 'screenChange',
        data: { previousScreen, currentScreen: newScreen },
      });
    }
  }

  private _emitEvent(event: AppShellEvent): void {
    // Snapshot listeners to avoid issues if a callback unsubscribes during iteration
    const listeners = [...this._shellListeners];
    for (const listener of listeners) {
      listener(event);
    }
  }
}
